import * as React from "react";
import { useSelect } from "@hubper/theme";
import { useFileBrowser, useFileDrop } from "../../hooks";
import Icon, { IconName } from "../../elements/Icon";
import Link from "../../elements/Link";
import Text from "../../elements/Text";
import ProgressCircle from "../../elements/ProgressCircle";

import { validateFile, Restrictions, ValidationError } from "./fileTypes";
import {
  MainText,
  OpenFileBrowserText,
  Overlay,
  Inner,
  DropBoxField
} from "./styles";
import posed, { PoseGroup } from "react-pose";

type Texts = {
  or: string;
  openFileBrowser: string;
  letGo: string;
  dragAndDrop: string;
  success: string;
  busy: string;
  notAllowedFileType: string;
  notAllowedFileSize: string;
  apiError: string;
  reset: string;
};

export const DEFAULT_TEXTS: Texts = {
  or: "of",
  openFileBrowser: "kies een bestand",
  letGo: "Laat maar vallen!",
  dragAndDrop: "Drag & Drop",
  success: "Gelukt!",
  busy: "Bezig...",
  notAllowedFileType: "Dit formaat is niet toegestaan",
  notAllowedFileSize: "Het geselecteerde bestand is te groot",
  apiError: "Fout bij het uploaden",
  reset: "Opnieuw proberen"
};

type UploadConfig = {
  file: File;
  category: string;
  onProgress: (progress: number) => void;
};

type TimeoutRef = React.MutableRefObject<number | null>;

const wait = (duration: number, timeoutRef: TimeoutRef) => {
  return new Promise(resolve => {
    timeoutRef.current = setTimeout(() => {
      resolve();
    }, duration);
  });
};

type UploadFileConfig = {
  file: File;
  category: string;
  uploadFunc: DropBoxProps["onUpload"];
  shouldIgnoreProgress: () => boolean;
  onSuccess: (payload: any) => void;
  onError: (err: any) => void;
  onProgress: (progres: number) => void;
};

const uploadFile = ({
  file,
  category,
  uploadFunc,
  shouldIgnoreProgress,
  onProgress,
  onSuccess,
  onError
}: UploadFileConfig) => {
  uploadFunc({
    file,
    category,
    onProgress: (progress: number) => {
      if (shouldIgnoreProgress()) {
        return;
      }

      onProgress(progress);
    }
  })
    .then(payload => {
      if (shouldIgnoreProgress()) {
        return;
      }
      onSuccess(payload);
    })
    .catch(err => {
      if (shouldIgnoreProgress()) {
        return;
      }
      onError(err);
    });
};

function useUnmount() {
  const isUnmountingRef = React.useRef(false);
  const timeoutRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    return () => {
      isUnmountingRef.current = true;

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    shouldIgnoreProgress: () => isUnmountingRef.current,
    timeoutRef
  };
}

export type StateName =
  | "IDLE"
  | "ERROR"
  | "API_ERROR"
  | "UPLOADING"
  | "COMPLETE";

export type State = {
  state: StateName;
  progress: number;
  mainText: string;
  id: number;
};

export type ActionType =
  | "SET_IDLE"
  | "SET_ERROR"
  | "SET_UPLOADING"
  | "SET_COMPLETE"
  | "SET_API_ERROR"
  | "UPDATE_PROGRESS";

export type Action = {
  type: ActionType;
  payload?: any;
};

const createInitialState = (texts: Texts): State => ({
  state: "IDLE",
  progress: 0,
  mainText: texts.dragAndDrop,
  id: Date.now()
});

const createReducer = (texts: Texts) => {
  return (state: State, action: Action): State => {
    switch (action.type) {
      case "SET_IDLE":
        return createInitialState(texts);
      case "SET_ERROR":
        return {
          ...state,
          state: "ERROR",
          mainText: texts[action.payload as ValidationError]
        };
      case "SET_UPLOADING":
        return { ...state, state: "UPLOADING", mainText: texts.busy };
      case "SET_COMPLETE":
        return {
          ...state,
          progress: 100,
          state: "COMPLETE",
          mainText: texts.success
        };
      case "SET_API_ERROR":
        return { ...state, state: "API_ERROR", mainText: texts.apiError };
      case "UPDATE_PROGRESS":
        return { ...state, progress: action.payload };
      default:
        throw new Error(`action ${action.type} does not exist`);
    }
  };
};

const IconWrapper = posed.div({
  enter: { opacity: 1, scale: 1, delay: 200 },
  exit: { opacity: 0, scale: 0.9 }
});

type StatusIconProps = {
  state: StateName;
  icon: IconName;
};

const StatusIcon = ({ state, icon }: StatusIconProps) => {
  return (
    <PoseGroup>
      {state === "COMPLETE" ? (
        <IconWrapper key="complete" withParent={false}>
          <Icon size="25" name="done" />
        </IconWrapper>
      ) : state === "ERROR" || state === "API_ERROR" ? (
        <IconWrapper key="error" withParent={false}>
          <Icon size="15" name="cross" />
        </IconWrapper>
      ) : (
        <IconWrapper key="idle" withParent={false}>
          <Icon size="25" name={icon} />
        </IconWrapper>
      )}
    </PoseGroup>
  );
};

export type DropBoxProps = {
  style?: React.CSSProperties;
  className?: string;
  texts?: Texts;
  icon?: IconName;
  restrictions?: Restrictions;
  category: string;
  onUpload: (config: UploadConfig) => Promise<any>;
  stretch?: boolean;
  onError?: (err: any) => void;
  onSuccess?: (payload: any) => void;
  timeToReset?: number;
};

function DropBox(
  // @ts-ignore
  {
    style,
    className,
    texts = DEFAULT_TEXTS,
    onUpload,
    category,
    stretch,
    icon = "upload",
    restrictions,
    timeToReset = 1000,
    onError,
    onSuccess
  }: DropBoxProps,
  ref: any
) {
  const { color } = useSelect();

  const [{ progress, mainText, state, id }, dispatch] = React.useReducer(
    createReducer(texts),
    createInitialState(texts)
  );

  // - timeoutRef for clearing timeout on unmount
  // - shouldIgnoreProgress for preventing state updates on
  //   unmounting
  const { timeoutRef, shouldIgnoreProgress } = useUnmount();

  const handleFile = (file: File) => {
    // check wether file is valid
    const possibleError = validateFile(file, restrictions);
    if (possibleError) {
      dispatch({ type: "SET_ERROR", payload: possibleError });
      return;
    }

    // proceed with uploading...
    dispatch({ type: "SET_UPLOADING" });
    uploadFile({
      file,
      category,
      uploadFunc: onUpload,
      shouldIgnoreProgress,
      onProgress: progress =>
        dispatch({ type: "UPDATE_PROGRESS", payload: progress }),
      onSuccess: async payload => {
        dispatch({ type: "SET_COMPLETE" });
        await wait(timeToReset ? timeToReset : 0, timeoutRef);
        if (onSuccess) {
          if (shouldIgnoreProgress()) {
            return;
          }
          onSuccess(payload);
        }
        dispatch({ type: "SET_IDLE" });
      },
      onError: err => {
        dispatch({ type: "SET_API_ERROR", payload: err });
        if (onError) {
          onError(err);
        }
      }
    });
  };

  const { bind, isOver } = useFileDrop(handleFile);
  const fileBrowserHandler = useFileBrowser(handleFile);

  return (
    <DropBoxField
      {...bind}
      data-testid="dropbox"
      ref={ref}
      state={state}
      style={style}
      stretch={stretch}
      isOver={isOver}
      className={className}
    >
      {isOver && (
        <Overlay>
          <Text fw="bold" fs={2} c={["grey", 8]} uppercase>
            {texts.letGo}
          </Text>
        </Overlay>
      )}
      <Inner>
        <ProgressCircle
          key={id}
          backgroundColor="white"
          colorActive={color(["success", 4])}
          colorInactive={color(["grey", 1])}
          size="40px"
          lineSize="6px"
          style={{
            position: "relative"
          }}
          progress={progress}
        >
          <StatusIcon state={state} icon={icon} />
        </ProgressCircle>
        <MainText>
          <Text fw="heavy" fs={0} c={["grey", 8]} uppercase>
            {mainText}
          </Text>
        </MainText>
        {state === "IDLE" && (
          <OpenFileBrowserText state={state}>
            <Text fs={-1} c={["grey", 4]}>
              {texts.or}{" "}
              <Link onClick={fileBrowserHandler} fs={-1} c={["grey", 4]}>
                {texts.openFileBrowser}
              </Link>
            </Text>
          </OpenFileBrowserText>
        )}
        {state === "API_ERROR" ||
          (state === "ERROR" && (
            <Link
              onClick={() => dispatch({ type: "SET_IDLE" })}
              fs={-1}
              c={["grey", 4]}
              style={{ display: "block" }}
            >
              {texts.reset}
            </Link>
          ))}
      </Inner>
    </DropBoxField>
  );
}

export default React.forwardRef(DropBox);
